import { useState } from 'react';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_CAISSE_TRIP_SUN } from '@/lib/import-templates';
import {
  useImportCaisseTripSun,
  type ImportCaisseTripSunRow,
} from '@/hooks/queries/useImportCaisse';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const FIELDS: ImportField[] = TEMPLATE_CAISSE_TRIP_SUN.headers.map((h) => ({
  key: h,
  label: h,
}));

export function ImportCaisseTripSunDialog({ open, onOpenChange }: Props): JSX.Element {
  const importMut = useImportCaisseTripSun();
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  return (
    <>
      {progress && progress.total > 0 && (
        <div className="fixed bottom-6 right-6 z-50 rounded-xl border border-blue-300 bg-white p-4 shadow-2xl">
          <div className="mb-1 text-xs font-bold uppercase tracking-widest text-blue-700">
            Import Trip Sun en cours
          </div>
          <div className="text-sm font-semibold text-slate-800">
            {progress.current} / {progress.total} lignes traitées
          </div>
          <div className="mt-2 h-1.5 w-64 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}
      <ImportDialog<ImportCaisseTripSunRow>
        open={open}
        onOpenChange={(o) => {
          onOpenChange(o);
          if (!o) setProgress(null);
        }}
        title="Importer la Caisse Trip Sun (CSV)"
        fields={FIELDS}
        generateTemplate={() => buildTemplate(TEMPLATE_CAISSE_TRIP_SUN)}
        templateFilename={TEMPLATE_CAISSE_TRIP_SUN.filename}
        requiredHeaders={TEMPLATE_CAISSE_TRIP_SUN.requiredHeaders}
        onImport={async (rows) => {
          setProgress({ current: 0, total: rows.length });
          try {
            const r = await importMut.mutateAsync({
              rows,
              onProgress: (p) => setProgress(p),
            });
            const summary: string[] = [];
            summary.push(
              `✓ ${r.entrees} entrée(s) · ${r.sorties} sortie(s) · ${r.frais_gouv} frais gouv`,
            );
            if (r.tiers_crees > 0) summary.push(`${r.tiers_crees} tier(s) créé(s)`);
            if (r.teleops_creees > 0)
              summary.push(`${r.teleops_creees} téléopératrice(s) créée(s)`);
            const msg = summary.join(' · ');
            const errs = [msg, ...r.errors];
            return { success: r.importees, errors: r.errors.length === 0 ? [msg] : errs };
          } finally {
            setProgress(null);
          }
        }}
      />
    </>
  );
}
